<template>
  <GenericWrapperChart :config="config">
    <template
      v-if="!$store.state.charts.engagementMatrix.isLoading"
      #title
    >
      <h1>
        {{ title }}
        <v-tooltip
          bottom
          content-class="engagement-matrix__tooltip"
        >
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <DsIcon
                class="engagement-matrix__tooltip-icon"
                name="questions"
              />
            </span>
          </template>

          <p class="engagement-matrix__tooltip-content">
            O índice de engajamento é calculado a partir {{ tooltipText.engagementRate }}
            <br><br>
            O índice de sentimento é calculado a partir da {{ tooltipText.polarityRate }}
          </p>
        </v-tooltip>
      </h1>
    </template>

    <template
      v-if="showFeedbackCard"
      #afterChart
    >
      <FeedbackCard :message="'O Instagram não está incluso no gráfico, pois a API da rede social não fornece o dado de sentimento nos comentários.'" />
    </template>
  </GenericWrapperChart>
</template>

<script>
import { DsIcon } from '@ds/icons';
import { ExportToCsv } from 'export-to-csv';
import { json2excel } from 'js2excel';
import { parseISO, format } from 'date-fns';

import { generateFileName, isRunningProductionEnvironment, numberFormatToPtBr } from '@/utils';
import { GenericWrapperChart } from '@/components/content/GenericWrapperChart';
import { FeedbackCard } from '@/components/base';

export default {
  name: 'EngagementMatrix',
  components: {
    GenericWrapperChart,
    DsIcon,
    FeedbackCard,
  },
  data() {
    return {
      title: 'Matriz de engajamento por publicações',
      quadrantBottomPosition: null,
      quadrantTopPosition: null,
    };
  },
  computed: {
    showFeedbackCard() {
      return this.$store.state.ui.channel.toLowerCase() === 'todas' && !isRunningProductionEnvironment;
    },
    tooltipText() {
      const socialName = this.$store.state.ui.channel.toLowerCase();
      const polarityRateTextDefault = 'subtração das publicações positivas e negativas, desconsiderando as publicações neutras.';

      const tooltipTextBySocialNetwork = {
        default: {
          engagementRate: 'do total de (curtidas + comentários x 1,5 + compartilhamentos x 2), dividido pelo número de fãs e multiplicado por 1.000.',
          polarityRate: polarityRateTextDefault,
        },
        youtube: {
          engagementRate: 'do total de (curtidas + comentários x 1,5), dividido pelo número de views e multiplicado por 1.000.',
          polarityRate: polarityRateTextDefault,
        },
        todas: {
          engagementRate: 'da soma das interações (curtidas + comentários x 1,5 + compartilhamentos x 2), dividido pela soma de fãs/views e multiplicado por 1.000 de todas as redes.',
          polarityRate: 'subtração das publicações positivas e negativas, desconsiderando as publicações neutras.',
        },
      };

      return socialName in tooltipTextBySocialNetwork ? tooltipTextBySocialNetwork[socialName] : tooltipTextBySocialNetwork.default;
    },
    fileName() {
      return generateFileName(this.title);
    },
    overrideChartOptions() {
      return {
        exporting: {
          enabled: false,
          sourceWidth: 950,
          filename: this.fileName,
          allowHTML: true,
          chartOptions: {
            chart: {
              marginRight: 50,
              styleMode: true,
              borderRadius: 0,
              plotBorderWidth: 1,
              spacingBottom: 3,
              spacingLeft: 0,
              spacingRight: 1,
              height: 493,
            },
            title: {
              text: this.title,
            },
            legend: {
              itemDistance: 16,
              itemStyle: {
                fontSize: 10,
              },
            },
            xAxis: {
              labels: {
                x: 0,
              },
            },
            yAxis: {
              maxPadding: 0,
              title: {
                useHTML: false,
              },
            },
          },
        },
      };
    },
    series() {
      return this.$store.state.charts.engagementMatrix.data.series;
    },
    config() {
      const isLoading = this.$store.state.charts.engagementMatrix.isLoading;
      const description = isLoading ? '' : 'Entenda o engajamento e sentimentos nos comentários das publicações.';
      const errorPidLimit = this.$store.state.charts.engagementMatrix.hasError.pidLimit;
      const hasError = this.$store.state.charts.engagementMatrix.hasError.default || errorPidLimit;
      const isEmpty = !Array.isArray(this.series) && !hasError;

      const downloadList = isRunningProductionEnvironment
        ? [{ label: 'PNG', type: 'png' }, { label: 'PDF', type: 'pdf' }]
        : [
          { label: 'PNG', type: 'png' },
          { label: 'PDF', type: 'pdf' },
          { label: 'CSV', type: 'csv-engagement-matrix' },
          { label: 'XLS', type: 'xls-engagement-matrix' },
        ];

      return {
        componentName: 'EngagementMatrix',
        title: '',
        description,
        isLoading,
        hasError,
        isEmpty,
        onError: this.getEngagementMatrixData,
        errorType: errorPidLimit ? 'ErrorPidLimit' : null,
        requestOnMounted: this.getEngagementMatrixData,
        downloadMenu: {
          onSelectDownload: this.onSelectDownload,
          showDownloadMenu: true,
          downloadList,
          trackNameOnOpen: 'BM ExportEngagementMatrix Graph Open',
          trackNameOnDownload: 'BM ExportEngagementMatrix Graph Click',
        },
        chart: {
          type: 'Bubble',
          series: this.series,
          metadata: {
            ...this.$store.state.charts.engagementMatrix.data.metadata,
            quadrantBottomPosition: this.quadrantBottomPosition,
            quadrantTopPosition: this.quadrantTopPosition,
          },
          overrideChartOptions: this.overrideChartOptions,
          trackNameOnObserve: 'BM EngagementMatrix Graph View',
          trackNameOnLegendClick: 'BM EngagementMatrix LegendGraph Click',
        },
      };
    },
    dataToExport() {
      return this.series.map((item) => {
        const [info] = item.data;

        return {
          'Data início': this.formatDate(this.$store.state.ui.dateRange.start),
          'Data fim': this.formatDate(this.$store.state.ui.dateRange.end),
          'Nome do grupo': info.name,
          'Total de publicações': numberFormatToPtBr(info.z),
          'Curtidas': numberFormatToPtBr(info.metrics.likesCount),
          'Comentários': numberFormatToPtBr(info.metrics.commentsCount),
          'Compartilhamentos': numberFormatToPtBr(info.metrics.sharesCount),
          '% Sentimento positivo': numberFormatToPtBr(info.polarity.positive),
          '% Sentimento Negativo': numberFormatToPtBr(info.polarity.negative),
          'Índice de engajamento': numberFormatToPtBr(info.y),
          '% Variação Índice de engajamento': numberFormatToPtBr(info.metrics.engagementRatePercent),
          'Índice de sentimento': numberFormatToPtBr(info.x),
          '% Variação Índice de sentimento': numberFormatToPtBr(info.metrics.polarityPercent),
        };
      });
    },
  },
  methods: {
    async getEngagementMatrixData() {
      await this.$store.dispatch('charts/fetchEngagementMatrix');
    },
    incrementMetadata() {
      this.quadrantBottomPosition = -37;
      this.quadrantTopPosition = 25;

      window.setTimeout(() => {
        this.quadrantBottomPosition = null;
        this.quadrantTopPosition = null;
      }, 100);
    },
    formatDate(date) {
      return format(parseISO(date), 'dd/LL/yyyy');
    },
    downloadCSV() {
      const options = {
        fieldSeparator: ';',
        showLabels: true,
        useKeysAsHeaders: true,
        useBom: true,
        filename: this.fileName,
      };

      new ExportToCsv(options).generateCsv(this.dataToExport);
    },
    downloadXLS() {
      const fileName = generateFileName('Matriz Engajamento');

      json2excel({
        data: this.dataToExport,
        name: fileName,
      });
    },
    onSelectDownload({ typeSelected }) {
      const downloadTypes = {
        'csv-engagement-matrix': this.downloadCSV,
        'xls-engagement-matrix': this.downloadXLS,
      };

      if (typeSelected in downloadTypes) {
        return downloadTypes[typeSelected]();
      }

      this.incrementMetadata();
    },
  },
};
</script>

<style lang="scss" scoped>
.engagement-matrix {
  &__tooltip {
    max-width: 246px;
    padding: var(--ds-spacing-inline-nano);
    border-radius: var(ds-border-radius--sm);
    font-size: var(--ds-font-size-xs);
    line-height: var(--ds-spacing-inline-xs);
    opacity: 1;
    box-shadow: var(--ds-light-dropshadow-1);
    background-color: var(--ds-color-text-1);

    &::after {
      content: "";
      position: absolute;
      bottom: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-bottom-color: var(--ds-color-text-1);
    }
  }

  &__tooltip-icon {
    cursor: pointer;
    height: var(--ds-spacing-stack-xs);
    width: var(--ds-spacing-stack-xs);
  }

  &__tooltip-content {
    color: var(--ds-color-text-neutral);
    font-size: var(--ds-font-size-xs);
    margin-bottom: 0;
  }
}
</style>
